import Alpine from 'alpinejs';

Alpine.data('homeReel', () => ({
  total: 0,
  slides: [],
  current: 0,
  animating: false,
  timer: null,
  raf: null,
  delay: 2500,       // ms to hold each slide
  duration: 1800,    // ms for the transition

  init() {
    const allSlides = [...this.$el.querySelectorAll('.homeReel__slide')];
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const hasMobileSlides = allSlides.some(s => s.hasAttribute('data-has-mobile'));

    if (isMobile && hasMobileSlides) {
      // On mobile, only show slides that have a mobile image
      this.slides = allSlides.filter(s => s.hasAttribute('data-has-mobile'));
      allSlides.filter(s => !s.hasAttribute('data-has-mobile')).forEach(s => {
        s.style.display = 'none';
      });
    } else if (!isMobile && hasMobileSlides) {
      // On desktop, show all slides
      this.slides = allSlides;
    } else {
      this.slides = allSlides;
    }

    this.total = this.slides.length;
    if (this.total < 1) return;

    // Preload images for smoother transitions
    this.slides.forEach(slide => {
      const img = slide.querySelector('img');
      if (img && !img.complete) {
        img.loading = 'eager';
      }
    });

    // Set first slide visible
    this.applyEnter(this.slides[0], 1);
    this.slides[0].classList.add('is-active');

    if (this.total > 1) {
      // Wait for splash animation to finish before starting the reel.
      // Path animations: 0–0.8s, splashShrink: 0.8–1.6s
      const splashActive = document.querySelector('.splash-intro.animate');
      const splashDelay = splashActive ? 1600 : 0;
      setTimeout(() => this.startTimer(), splashDelay);
    }
  },

  destroy() {
    this.stopTimer();
    if (this.raf) cancelAnimationFrame(this.raf);
  },

  startTimer() {
    this.stopTimer();
    this.timer = setTimeout(() => this.advance(), this.delay);
  },

  stopTimer() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  },

  advance() {
    if (this.animating || this.total < 2) return;
    this.animating = true;

    const from = this.current;
    const to = (this.current + 1) % this.total;
    const start = performance.now();

    // Fade out meta immediately via CSS
    this.slides[from].classList.add('is-exiting');

    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / this.duration, 1);

      // Stagger: exit starts immediately, enter is delayed slightly
      // so the outgoing slide begins to shrink before the new one appears
      const exitProgress = progress;
      const enterProgress = Math.max(0, (progress - 0.15) / 0.85);

      this.applyExit(this.slides[from], exitProgress);
      this.applyEnter(this.slides[to], enterProgress);

      if (progress < 1) {
        this.raf = requestAnimationFrame(tick);
      } else {
        this.slides[from].classList.remove('is-exiting');
        this.slides[from].classList.remove('is-active');
        this.slides[to].classList.add('is-active');
        this.current = to;
        this.animating = false;
        this.startTimer();
      }
    };

    this.raf = requestAnimationFrame(tick);
  },

  // Entering: scale up from centre-bottom
  applyEnter(slide, progress) {
    if (progress <= 0) {
      slide.style.visibility = 'hidden';
      slide.style.clipPath = 'inset(100% 50% 0% 50%)';
      slide.style.transform = 'scale(0.35)';
      slide.style.zIndex = '0';
      return;
    }

    slide.style.visibility = 'visible';
    slide.style.zIndex = progress >= 1 ? '1' : '2';
    slide.style.transformOrigin = 'center bottom';

    const ease = this.easeInOutQuart(progress);

    const top = 100 * (1 - ease);
    const side = 50 * (1 - ease);
    slide.style.clipPath = `inset(${top}% ${side}% 0% ${side}%)`;

    const scale = 0.35 + 0.65 * ease;
    slide.style.transform = `scale(${scale})`;
  },

  // Exiting: scale down to centre-top
  applyExit(slide, progress) {
    if (progress <= 0) {
      slide.style.visibility = 'visible';
      slide.style.clipPath = 'inset(0% 0% 0% 0%)';
      slide.style.transform = 'scale(1)';
      slide.style.zIndex = '1';
      return;
    }

    if (progress >= 1) {
      slide.style.visibility = 'hidden';
      slide.style.clipPath = 'inset(0% 50% 100% 50%)';
      slide.style.transform = 'scale(0.35)';
      slide.style.zIndex = '0';
      return;
    }

    slide.style.visibility = 'visible';
    slide.style.zIndex = '1';
    slide.style.transformOrigin = 'center top';

    const ease = this.easeInOutQuart(progress);

    const bottom = 100 * ease;
    const side = 50 * ease;
    slide.style.clipPath = `inset(0% ${side}% ${bottom}% ${side}%)`;

    const scale = 1 - 0.65 * ease;
    slide.style.transform = `scale(${scale})`;
  },

  // Smooth ease-in-out — gentle start AND end (no abrupt snap)
  easeInOutQuart(t) {
    return t < 0.5
      ? 8 * t * t * t * t
      : 1 - Math.pow(-2 * t + 2, 4) / 2;
  },
}));
